/**
 * Data types for hooks and components
 * Centralized type definitions for bucket, position, trade, and price data
 */

import { RawBucket, OrderType, MultiFeedBucketData, RawOrder, RawPosition, MultiFeedPositionsData, RawTrade } from "./api";
import {
  toNumberFromSatoshi,
  parseNumericValue,
  toBigInt,
  fromSatoshi,
} from "../utils/math";
import { normalizeProtobufTimestamp, normalizeTimestamp } from "../utils/time";
import type { SubscriptionClient } from "../api/subscription/SubscriptionClient";
import {
  ORDER_SIDE_LONG,
  ORDER_SIDE_SHORT,
  MARGIN_MODE_CROSS,
} from "../../constants";

// ============================================================================
// BUCKET TYPES
// ============================================================================

/**
 * Configuration for bucket collections hook
 */
export interface BucketCollectionsConfig {
  subscriptionClient?: SubscriptionClient; // Optional: hook uses useSubscription() when inside SubscriptionProvider
  baseURL: string;
  walletAddress: string;
  authToken?: string;
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string | number; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

/**
 * Full bucket data structure
 * Contains all bucket information from API
 */
/** Map collateral asset index (API) to ticker for display. */
const COLLATERAL_INDEX_TO_TICKER: Record<string, string> = {
  "1": "USDC",
  "2": "USDT",
};

export interface Bucket {
  bucketId: string;
  address: string;
  marginType: string; // e.g., "cross", "isolated"
  collateralAssetIndex: string; // API index e.g. "1"
  collateralAsset: string; // derived ticker e.g. "USDC"
  depositedMargin: number; // Total margin deposited
  totalEquity: number;
  availableMargin: number;
  usedMargin: number;
  unrealizedProfit: number;
  unrealizedLoss: number;
  realizedProfit: number;
  realizedLoss: number;
  unrealizedPnL: number; // Calculated: unrealizedProfit - unrealizedLoss
  marginRatio: number;
  imr: number; // Initial Margin Requirement (as number, e.g., 10 for 10%)
  totalNotionalValue: number;
  riskLevel: string;
  maxLeverage: number;
  correlationOffset: number;
  status: string; // e.g., "dormant", "active"
  createdAt: string; // ISO timestamp string
  updatedAt: string; // ISO timestamp string
  // Legacy/computed fields for backward compatibility
  leverage: number; // Alias for maxLeverage
  marginMode: string; // Alias for marginType
  raw: any;
}

/**
 * Configuration for buckets hook
 */
export interface BucketsConfig {
  subscriptionClient?: SubscriptionClient; // Optional: from useSubscription() when inside SubscriptionProvider
  baseURL: string;
  walletAddress: string;
  authToken: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket: boolean; // Use WebSocket as primary source (default: true)
  marketId: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

// ============================================================================
// POSITION TYPES
// ============================================================================

/**
 * Position data structure
 * Represents a trading position
 */
export interface Position {
  positionId: string; // Position identifier (from API position_id field)
  marketId: string;
  bucketId: string; // Bucket ID associated with the position
  marginMode: string; // e.g., 'CROSS', 'ISOLATED'
  symbol: string; // May not be in API response, will need to map from marketId
  size: number; // Decimal value
  entryPrice: number; // Decimal value
  markPrice: number; // Decimal value
  liquidationPrice: number; // Decimal value
  unrealizedPnL: number; // Decimal value
  leverage: number; // Decimal value
  margin: number; // Calculated or from API
  side: "LONG" | "SHORT"; // Inferred from size sign
  raw?: any; // Store raw data for modal display
}

/**
 * Configuration for positions hook
 */
export interface PositionsConfig {
  subscriptionClient?: SubscriptionClient; // Optional: hook uses useSubscription() when inside SubscriptionProvider
  baseURL: string;
  authToken?: string;
  walletAddress?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

// ============================================================================
// TRADE PRICE TYPES
// ============================================================================

/**
 * Last trade price data
 * Represents the most recent trade execution
 */
export interface LastPriceData {
  marketIndex: string;
  price: number;
  quantity: number;
  timestamp: number;
  isBuyerMaker: boolean;
  tradeId: string;
}

/**
 * Mark price data
 * Represents the current mark price for a symbol
 */
export interface MarkPriceData {
  symbol: string;
  markPrice: number; // Mark price as decimal number (converted from satoshi)
  timestamp: number;
}

/**
 * Configuration for trade price hook
 * Symbol is derived from marketId using useMarkets hook
 */
export interface TradePriceConfig {
  subscriptionClient?: SubscriptionClient; // Optional: from context when inside SubscriptionProvider
  baseURL: string;
  marketId: string;
  limit?: number;
  disablePolling?: boolean; // If true, disable HTTP polling fallback when WebSocket is disconnected
}

/**
 * Props for TradePrice component.
 * subscriptionClient is optional; when inside SubscriptionProvider, context is used for subscriptions.
 * Symbol is derived from marketId using useMarkets hook.
 */
export interface TradePriceProps {
  subscriptionClient?: SubscriptionClient; // Optional: from context when inside SubscriptionProvider
  baseURL?: string;
  marketId?: string;
  limit?: number;
}

/**
 * Recent trade data for display in trade table
 */
export interface RecentTradeDos {
  time: number;
  size: number;
  price: number;
  isBuyerMaker: boolean;
}

/**
 * Transform RawTrade array to RecentTradeDos array for display
 * Converts satoshi strings to decimal numbers and normalizes timestamps
 * Sorts trades by timestamp descending (newest first)
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformRawTradesToRecentTradeDos(
  rawTrades: RawTrade[],
): RecentTradeDos[] {
  if (!rawTrades || !Array.isArray(rawTrades) || rawTrades.length === 0) {
    return [];
  }

  // Sort by timestamp descending (newest first)
  const sortedTrades = [...rawTrades].sort((a, b) => {
    const timeA = normalizeTimestamp(a.timestamp);
    const timeB = normalizeTimestamp(b.timestamp);
    return timeB - timeA; // Descending: newest first
  });

  // Convert to display format
  return sortedTrades.map((trade: RawTrade): RecentTradeDos => {
    // Handle timestamp - convert string to number and ensure milliseconds
    const timestamp = normalizeTimestamp(
      trade.timestamp as string | number,
    );

    // Convert satoshi strings to decimal numbers
    const size: number = toNumberFromSatoshi(trade.quantity);
    const price: number = toNumberFromSatoshi(trade.price);

    return {
      time: timestamp,
      size,
      price,
      isBuyerMaker: false,
    };
  });
}

// ============================================================================
// ORDER TYPES
// ============================================================================

/**
 * Pending order data structure
 * Represents an order that is pending, open, or partially filled
 * Transformed from RawOrder for use in hooks and components
 */
export interface PendingOrder {
  id: string;
  orderId: string;
  marketId: string;
  side: "BUY" | "SELL" | "LONG" | "SHORT";
  type: OrderType;
  quantity: number;
  price: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  timeInForce: string;
  createdAt: number;
  updatedAt: number;
  raw?: any;
}

/**
 * Configuration for pending orders hook
 */
export interface PendingOrdersConfig {
  subscriptionClient?: SubscriptionClient; // Optional: from context when inside SubscriptionProvider
  baseURL: string;
  authToken?: string;
  walletAddress?: string; // Optional - if not provided, API may derive from auth token
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
}

/**
 * Transform API/WS response to Position format
 * Converts Satoshi strings to decimal numbers for display
 * Handles both RawPosition (from HTTP API) and MultiFeedPositionsData (from WebSocket)
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformPosition(
  pos: RawPosition | MultiFeedPositionsData,
): Position {
  // Normalize position data - handle both RawPosition and MultiFeedPositionsData (proto camelCase)
  let positionId: string;
  if ("positionId" in pos && pos.positionId) {
    positionId = pos.positionId;
  } else {
    // Generate fallback ID if missing (for WebSocket updates without positionId)
    positionId = `${pos.marketIndex}-${pos.bucketId || "default"}-${pos.address}`;
  }

  // Get entry price - RawPosition uses averageEntryPrice, MultiFeedPositionsData uses entryPrice
  const entryPriceStr =
    "averageEntryPrice" in pos
      ? pos.averageEntryPrice
      : pos.entryPrice;

  // Get side - RawPosition uses side (string), MultiFeedPositionsData uses positionSide
  let side: "LONG" | "SHORT";
  if ("positionSide" in pos && pos.positionSide) {
    side = pos.positionSide;
  } else if ("side" in pos && pos.side) {
    // RawPosition format: "SIDE_BUY" (long) or "SIDE_SELL" (short)
    side =
      pos.side === "SIDE_BUY" || pos.side.toUpperCase().includes("BUY")
        ? ORDER_SIDE_LONG
        : ORDER_SIDE_SHORT;
  } else {
    // Fallback: infer from size sign
    const size = toNumberFromSatoshi(pos.size);
    side = size >= 0 ? ORDER_SIDE_LONG : ORDER_SIDE_SHORT;
  }

  // Convert size
  const size = toNumberFromSatoshi(pos.size);

  // Convert numeric fields from Satoshi strings to decimals (proto camelCase)
  const entryPrice = toNumberFromSatoshi(entryPriceStr);
  const markPrice = toNumberFromSatoshi(pos.markPrice);
  const leverage = pos.leverage
    ? toNumberFromSatoshi(pos.leverage)
    : 0;

  // Calculate unrealized PnL from API format (unrealizedProfit - unrealizedLoss) or legacy unrealizedPnl
  let unrealizedPnL = 0;
  if (
    pos.unrealizedProfit !== undefined ||
    pos.unrealizedLoss !== undefined
  ) {
    const profitBigInt = toBigInt(pos.unrealizedProfit);
    const lossBigInt = toBigInt(pos.unrealizedLoss);
    const pnlBigInt = profitBigInt - lossBigInt;
    unrealizedPnL = toNumberFromSatoshi(pnlBigInt.toString());
  } else if ("unrealizedPnl" in pos && pos.unrealizedPnl !== undefined) {
    unrealizedPnL = toNumberFromSatoshi(pos.unrealizedPnl);
  }

  const marginBalance = toNumberFromSatoshi(pos.marginBalance);
  const margin =
    marginBalance ||
    (leverage && entryPrice && size !== 0
      ? Math.abs((Math.abs(size) * entryPrice) / leverage)
      : 0);

  const marginMode =
    ("marginMode" in pos && pos.marginMode) ||
    ("marginType" in pos && pos.marginType) ||
    MARGIN_MODE_CROSS;

  return {
    positionId,
    marketId: String(pos.marketIndex),
    symbol: pos.symbol || `m-${pos.marketIndex}`,
    size: Math.abs(size),
    entryPrice,
    markPrice,
    liquidationPrice: pos.liquidationPrice
      ? toNumberFromSatoshi(pos.liquidationPrice)
      : 0,
    unrealizedPnL,
    leverage,
    margin,
    side,
    marginMode,
    bucketId: pos.bucketId ? String(pos.bucketId) : "",
    raw: pos,
  };
}

/**
 * Transform RawOrder from API/WS response to PendingOrder format
 * Handles RawOrder structure from API (snake_case with enum values and string numbers)
 * Based on RawOrder interface from src/sdk/types/api.ts
 * Requires orderId to be set for display; marketIndex is required (proto camelCase).
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformRawOrderToPendingOrder(order: RawOrder): PendingOrder {
  const normalizedSide = order.side === "SIDE_BUY" ? "BUY" : "SELL";

  let normalizedStatus: string = order.status || "ORDER_STATUS_PENDING";
  if (normalizedStatus.includes("ORDER_STATUS_")) {
    normalizedStatus = normalizedStatus.replace("ORDER_STATUS_", "");
  }
  const rawTimeInForce = order.timeInForce;
  let normalizedTimeInForce: string | undefined = rawTimeInForce;
  if (rawTimeInForce && rawTimeInForce.includes("TIME_IN_FORCE_")) {
    normalizedTimeInForce = rawTimeInForce.replace("TIME_IN_FORCE_", "");
  }

  const parseNumber = (value: string | number | undefined): number =>
    toNumberFromSatoshi(value);

  const quantity = parseNumber(order.quantity);
  const price = parseNumber(order.price);
  const filledQuantity = parseNumber(order.filledQuantity);
  const remainingQuantity = quantity - filledQuantity;

  const orderId = order.orderId ?? "";
  const marketId = order.marketIndex ?? "";

  // Convert timestamp to number (milliseconds)
  // RawOrder.timestamp can be Timestamp object (with seconds/nanos) or string/number
  // PendingOrder requires createdAt and updatedAt as numbers (not undefined)
  // Use normalizeProtobufTimestamp utility which handles all timestamp formats
  const timestamp = normalizeProtobufTimestamp(order.timestamp, Date.now());
  return {
    id: orderId,
    orderId,
    marketId,
    side: normalizedSide,
    type: order.type,
    quantity,
    price,
    filledQuantity,
    remainingQuantity,
    status: normalizedStatus,
    timeInForce: normalizedTimeInForce ?? "",
    createdAt: timestamp,
    updatedAt: timestamp,
    raw: order,
  };
}

// ============================================================================
// TRADE OPERATION TYPES
// ============================================================================

/**
 * Configuration for trade operation hook
 */
export interface TradeOperationConfig {
  baseURL: string;
  mnemonic: string;
  marketId: string;
  marketSymbol?: string;
  selectedBucketBalance?: number; // Balance from selected bucket (nominalValue)
  selectedBucketId?: string; // Bucket ID for order placement (required for placeOrder)
  tradePrice?: number; // Trade price from useTradePrice hook for market orders
}

// ============================================================================
// ORDERBOOK TYPES
// ============================================================================

/**
 * Individual orderbook level (price, size, cumulative depth)
 */
export interface OrderbookLevel {
  price: number;
  size: number;
  cumulative: number;
  flash?: "up" | "down" | null; // Visual indicator for changed levels
}
export type FastNumberLevel = [number, number][];
/**
 * Complete orderbook data structure
 */
export interface OrderbookData {
  asks: OrderbookLevel[];
  bids: OrderbookLevel[];
  spread: number;
  midPrice: number;
  marketIndex: string;
  sequenceId: string;
  firstUpdateId: number;
  lastUpdateId: number;
  checksum: string;
  timestamp: string;
  snapshot?: boolean;
}

/**
 * Configuration for orderbook hook
 */
export interface OrderbookConfig {
  subscriptionClient?: SubscriptionClient; // Optional: hook uses useSubscription() when inside SubscriptionProvider
  baseURL: string;
  marketId: string;
  maxDepth?: number;
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  disablePolling?: boolean; // If true, disable HTTP polling fallback when WebSocket is disconnected
}

/**
 * Trade operation result
 */
export interface TradeResult {
  success: boolean;
  orderId?: string;
  error?: string;
  message?: string;
}

// ============================================================================
// FUNDING RATE TYPES
// ============================================================================

/**
 * Configuration for rates hook
 * Automatically subscribes to all rate sub-channels: funding_rate, deviation, twap
 */
export interface RateConfig {
  subscriptionClient?: SubscriptionClient; // Optional: hook uses useSubscription() when inside SubscriptionProvider
  baseURL: string;
  marketId: string; // Single market ID to subscribe to
  historyDepth?: number; // Number of historical updates to retain (default: 1 for latest only)
}

/**
 * Funding rate data structure
 */
export interface FundingRateData {
  symbol: string;
  marketIndex: string;
  marketType: string;
  fundingRate: string;
  markPrice: string;
  indexPrice: string;
  premiumRate: string;
  emaFundingRate: string;
  rawFundingRate: string;
  calculationTimeUnix: number;
  nextFundingTimeUnix: number;
  fundingIntervalSeconds: number;
  calculationMethod: string;
  isValid: boolean;
  isStale: boolean;
  calculationDetails?: Record<string, string>;
  optionType?: string;
  strikePrice?: string;
  markIv?: string;
  timeValue?: string;
  intrinsicValue?: string;
  fundingPremium?: string;
  raw?: any;
}

// ============================================================================
// KLINE TYPES
// ============================================================================

/**
 * Configuration for kline hook
 */
export interface KlineConfig {
  subscriptionClient?: SubscriptionClient; // Optional: hook uses useSubscription() when inside SubscriptionProvider
  marketId: string;
  interval: string; // "1m", "5m", "15m", "1h", "4h", "1d"
  symbol?: string; // Symbol for kline subscription (e.g., "BTC/USDC"), defaults to "BTC/USDC" if not provided
  maxDataPoints?: number; // Maximum number of data points to keep in memory (default: 100)
}

/**
 * Kline (OHLC candlestick) data structure
 */
export interface KlineData {
  marketIndex: string;
  symbol?: string;
  interval: string;
  openTime: number;
  closeTime: number;
  open: number; // Decimal value
  high: number; // Decimal value
  low: number; // Decimal value
  close: number; // Decimal value
  volume: number; // Decimal value
  tradeCount?: number;
  timestamp: number;
  raw?: any;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

import { timestampToISOString } from "../utils/time";

/**
 * Convert MultiFeedBucketData to RawBucket format
 * MultiFeedBucketData has fewer fields, so we provide defaults for missing fields
 * This is a pure function with no dependencies, suitable for use outside React hooks
 * @param multiFeedBucket - MultiFeedBucketData from WebSocket subscription
 * @returns RawBucket object with all required fields
 */
export function convertMultiFeedToRawBucket(
  multiFeedBucket: MultiFeedBucketData,
): RawBucket {
  const now = new Date().toISOString();
  return {
    bucketId: multiFeedBucket.bucketId,
    address: multiFeedBucket.address,
    bucketType: multiFeedBucket.marginType,
    collateralAssetIndex: "1",
    depositedMargin: "0",
    totalEquity: multiFeedBucket.totalEquity,
    availableMargin: multiFeedBucket.availableMargin,
    usedMargin: multiFeedBucket.usedMargin,
    unrealizedProfit: multiFeedBucket.unrealizedProfit,
    unrealizedLoss: multiFeedBucket.unrealizedLoss,
    realizedProfit: "0",
    realizedLoss: "0",
    marginRatio: "0",
    imr: "0",
    totalNotionalValue: "0",
    riskLevel: "low",
    maxLeverage: "0",
    correlationOffset: 0,
    status: "active",
    createdAt: now,
    updatedAt: now,
    sequenceId: "0",
  };
}

/**
 * Convert raw API bucket data (camelCase) to Bucket type.
 */
export function transformRawBucketToBucket(rawBucket: RawBucket): Bucket {
  const bucketId = rawBucket.bucketId ?? "---";
  const unrealizedProfitBigInt = toBigInt(rawBucket.unrealizedProfit ?? "0");
  const unrealizedLossBigInt = toBigInt(rawBucket.unrealizedLoss ?? "0");
  const unrealizedPnLBigInt = unrealizedProfitBigInt - unrealizedLossBigInt;
  const unrealizedPnL = fromSatoshi(unrealizedPnLBigInt.toString());
  const unrealizedProfit = toNumberFromSatoshi(rawBucket.unrealizedProfit ?? "0");
  const unrealizedLoss = toNumberFromSatoshi(rawBucket.unrealizedLoss ?? "0");
  const maxLeverage = parseNumericValue(rawBucket.maxLeverage);
  const marginType = rawBucket.bucketType ?? "cross";
  const collateralAssetIndex = rawBucket.collateralAssetIndex ?? "1";
  const collateralAsset =
    COLLATERAL_INDEX_TO_TICKER[collateralAssetIndex] ?? "USDC";
  return {
    bucketId,
    address: rawBucket.address || "",
    marginType: marginType.toLowerCase().includes("isolated") ? "isolated" : "cross",
    collateralAssetIndex,
    collateralAsset,
    depositedMargin: toNumberFromSatoshi(rawBucket.depositedMargin ?? "0"),
    totalEquity: toNumberFromSatoshi(rawBucket.totalEquity ?? "0"),
    availableMargin: toNumberFromSatoshi(rawBucket.availableMargin ?? "0"),
    usedMargin: toNumberFromSatoshi(rawBucket.usedMargin ?? "0"),
    unrealizedProfit,
    unrealizedLoss,
    realizedProfit: toNumberFromSatoshi(rawBucket.realizedProfit ?? "0"),
    realizedLoss: toNumberFromSatoshi(rawBucket.realizedLoss ?? "0"),
    unrealizedPnL,
    marginRatio: toNumberFromSatoshi(rawBucket.marginRatio ?? "0"),
    imr: toNumberFromSatoshi(rawBucket.imr ?? "0"),
    totalNotionalValue: toNumberFromSatoshi(rawBucket.totalNotionalValue ?? "0"),
    riskLevel: rawBucket.riskLevel || "",
    maxLeverage,
    correlationOffset: rawBucket.correlationOffset ?? 0,
    status: rawBucket.status || "",
    createdAt: timestampToISOString(rawBucket.createdAt),
    updatedAt: timestampToISOString(rawBucket.updatedAt),
    leverage: maxLeverage,
    marginMode: marginType,
    raw: rawBucket,
  };
}

/**
 * Calculate risk ratio for a bucket (used margin / total equity)
 * Returns a value between 0 and 1
 */
export function calculateRiskRatio(bucket: Bucket): number {
  return bucket.totalEquity > 0
    ? Math.min(Math.max(bucket.usedMargin / bucket.totalEquity, 0), 1)
    : 0;
}

/**
 * Calculate cross margin ratio for a bucket (used margin / total equity)
 */
export function calculateCrossMarginRatio(bucket: Bucket): number {
  return bucket.totalEquity > 0 ? bucket.usedMargin / bucket.totalEquity : 0;
}

/**
 * Get display margin type name from bucket
 */
export function getDisplayMarginType(bucket: Bucket): string {
  const marginTypeValue = bucket.marginMode || bucket.marginType || "cross";
  return marginTypeValue === "ISOLATED" || marginTypeValue === "isolated"
    ? "ISOLATED"
    : "CROSS";
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if a bucket is RawBucket
 */
export function isRawBucket(
  bucket: RawBucket | MultiFeedBucketData,
): bucket is RawBucket {
  return "collateralAssetIndex" in bucket;
}

/**
 * Type guard for LastPriceData
 */
export function isLastPriceData(value: any): value is LastPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.price === "number" &&
    typeof value.marketIndex === "string" &&
    typeof value.quantity === "number" &&
    typeof value.timestamp === "number" &&
    typeof value.isBuyerMaker === "boolean" &&
    typeof value.tradeId === "string"
  );
}

/**
 * Type guard for MarkPriceData
 */
export function isMarkPriceData(value: any): value is MarkPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.symbol === "string" &&
    typeof value.markPrice === "number" &&
    typeof value.timestamp === "number"
  );
}
